if (!customElements.get('ly-index-banner')) {
    customElements.define('ly-index-banner', class extends HTMLElement {

        connectedCallback() {
            this.interval = Number(this.dataset.interval) * 1000
            this._track = this.querySelector('.ly-index-banner-media-items')
            this._items = [...this.querySelectorAll('.ly-index-banner-media-item')]
            this._indicators = [...this.querySelectorAll('.ly-index-banner-mobile-indicator')]
            this.contents = [...this.querySelectorAll('[data-content]')]
            this._maxIndex = this._items.length - 1

            if (!this._track || !this._items.length) return

            this.#bindCarouselEvents()
            this._track.style.touchAction = 'pan-y'
            this.#render(false)
            this.#updateActiveState()

            this.#autoplayEnabled = !!(this.interval && !isNaN(this.interval) && this._items.length > 1)

            this.#bindPauseTriggers()

            if (this.#autoplayEnabled) {
                this.#restartAutoplay()
            }
        }

        disconnectedCallback() {
            this.#teardownAutoplay()
            this.#unbindCarouselEvents()
        }

        /* ================= transform 轮播 ================= */

        _index = 0
        _maxIndex = 0
        _isDragging = false
        #isAnimating = false
        #transitionTimer = null
        #resizeObserver = null
        #pointerId = null
        #startX = 0
        #startY = 0
        #dragX = 0
        #dragDirection = null
        #indicatorCleanups = []

        #bindCarouselEvents = () => {
            this._track.addEventListener('transitionend', this.#handleTransitionEnd)
            this._track.addEventListener('pointerdown', this.#handlePointerDown, { passive: true })
            this._track.addEventListener('pointermove', this.#handlePointerMove, { passive: false })
            this._track.addEventListener('pointerup', this.#handlePointerEnd)
            this._track.addEventListener('pointercancel', this.#handlePointerEnd)

            this.#indicatorCleanups = this._indicators.map((indicator, index) => {
                const handler = () => this.#goTo(index % this._items.length, false)
                indicator.addEventListener('click', handler)
                return () => indicator.removeEventListener('click', handler)
            })

            this.#resizeObserver = new ResizeObserver(() => this.#render(false))
            this.#resizeObserver.observe(this._track.parentElement)
        }

        #unbindCarouselEvents = () => {
            clearTimeout(this.#transitionTimer)
            this.#resizeObserver?.disconnect()
            this.#resizeObserver = null
            this.#indicatorCleanups.forEach(cleanup => cleanup())
            this.#indicatorCleanups = []

            this._track?.removeEventListener('transitionend', this.#handleTransitionEnd)
            this._track?.removeEventListener('pointerdown', this.#handlePointerDown)
            this._track?.removeEventListener('pointermove', this.#handlePointerMove)
            this._track?.removeEventListener('pointerup', this.#handlePointerEnd)
            this._track?.removeEventListener('pointercancel', this.#handlePointerEnd)
        }

        #getWidth = () => this._track.parentElement?.clientWidth || this._track.clientWidth

        #render = (animate = true, offset = 0) => {
            this._track.style.transition = animate ? 'transform 450ms ease' : 'none'
            this._track.style.transform = `translate3d(${-this._index * this.#getWidth() + offset}px, 0, 0)`
        }

        #prepareLoopItem = direction => {
            this._items.forEach(item => item.style.transform = '')

            if (direction === 1 && this._index === this._maxIndex) {
                this._items[0].style.transform = `translate3d(${this._items.length * 100}%, 0, 0)`
            } else if (direction === -1 && this._index === 0) {
                this._items[this._maxIndex].style.transform = `translate3d(${-this._items.length * 100}%, 0, 0)`
            }
        }

        #move = (direction, isAuto = false) => {
            if (this.#isAnimating || this._items.length <= 1) return

            const oldIndex = this._index
            const isLooping = (direction === 1 && oldIndex === this._maxIndex)
                || (direction === -1 && oldIndex === 0)

            this.#prepareLoopItem(direction)
            this._index = (oldIndex + direction + this._items.length) % this._items.length
            this.#isAnimating = true
            this.#isAutoAdvancing = isAuto

            this.#onIndexChanged(this._index)
            this.#isAutoAdvancing = false

            requestAnimationFrame(() => {
                if (isLooping) {
                    const physicalIndex = direction === 1 ? oldIndex + 1 : oldIndex - 1
                    this._track.style.transition = 'transform 450ms ease'
                    this._track.style.transform = `translate3d(${-physicalIndex * this.#getWidth()}px, 0, 0)`
                } else {
                    this.#render(true)
                }
            })

            clearTimeout(this.#transitionTimer)
            this.#transitionTimer = setTimeout(this.#finishTransition, 520)
        }

        #goTo = (index, isAuto = false) => {
            if (this.#isAnimating || index === this._index || index < 0 || index > this._maxIndex) return

            this._items.forEach(item => item.style.transform = '')
            this._index = index
            this.#isAnimating = true
            this.#isAutoAdvancing = isAuto
            this.#onIndexChanged(index)
            this.#isAutoAdvancing = false
            this.#render(true)

            clearTimeout(this.#transitionTimer)
            this.#transitionTimer = setTimeout(this.#finishTransition, 520)
        }

        #handleTransitionEnd = event => {
            if (event.target !== this._track || event.propertyName !== 'transform') return
            this.#finishTransition()
        }

        #finishTransition = () => {
            if (!this.#isAnimating) return
            clearTimeout(this.#transitionTimer)
            this.#isAnimating = false
            this._items.forEach(item => item.style.transform = '')
            this.#render(false)
        }

        #handlePointerDown = event => {
            if (this.#isAnimating || this._items.length <= 1 || event.button > 0) return

            this.#pointerId = event.pointerId
            this.#startX = event.clientX
            this.#startY = event.clientY
            this.#dragX = 0
            this.#dragDirection = null
            this._isDragging = true
            this._track.setPointerCapture?.(event.pointerId)
            this._track.style.transition = 'none'
        }

        #handlePointerMove = event => {
            if (!this._isDragging || event.pointerId !== this.#pointerId) return

            const dx = event.clientX - this.#startX
            const dy = event.clientY - this.#startY

            if (!this.#dragDirection) {
                if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return
                this.#dragDirection = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical'
            }

            if (this.#dragDirection !== 'horizontal') return

            event.preventDefault()
            this.#dragX = dx
            this.#prepareLoopItem(dx < 0 ? 1 : -1)
            this.#render(false, dx)
        }

        #handlePointerEnd = event => {
            if (!this._isDragging || event.pointerId !== this.#pointerId) return

            this._isDragging = false
            this.#pointerId = null

            if (this.#dragDirection !== 'horizontal') {
                this.#render(false)
                return
            }

            const threshold = this.#getWidth() * 0.15
            if (Math.abs(this.#dragX) >= threshold) {
                this.#move(this.#dragX < 0 ? 1 : -1)
            } else {
                this.#isAnimating = true
                this.#render(true)
                clearTimeout(this.#transitionTimer)
                this.#transitionTimer = setTimeout(this.#finishTransition, 520)
            }
        }

        #onIndexChanged = newIndex => {
            this.contents.forEach((c, i) => {
                c.classList.toggle('current', i === newIndex)
            })

            this.#updateActiveState()

            // 用户手动切换后，若新项是正在播放的视频 → 进度清零重新播放
            if (!this.#isAutoAdvancing) {
                this.#resetVideoIfPlaying(this._items[newIndex])
            }

            this.#restartAutoplay()
        }

        #updateActiveState = () => {
            this._items.forEach((item, index) => {
                item.classList.toggle('is-active', index === this._index)
            })

            this._indicators.forEach((indicator, index) => {
                const active = index % this._items.length === this._index
                indicator.classList.toggle('is-active', active)
                indicator.setAttribute('aria-current', active ? 'true' : 'false')
            })
        }

        /* ================= 自动播放状态 ================= */

        #autoplayEnabled = false
        #isPaused = false
        #isAutoAdvancing = false
        #mode = null               // 'countdown' | 'video'

        /* 倒计时模式 */
        #rafId = null
        #countdownStart = 0
        #countdownElapsed = 0
        #countdownDuration = 0

        /* 视频模式 */
        #videoEl = null
        #videoObserver = null

        #unbindPause = null

        /* ================= 启停 ================= */

        #restartAutoplay = () => {
            this.#clearCountdown()
            this.#clearVideoWaiting()
            this.#resetIndicators()

            if (!this.#autoplayEnabled) {
                this.#mode = null
                return
            }

            const item = this._items[this._index]
            this.#mode = item.querySelector('ly-video') ? 'video' : 'countdown'

            // 暂停中：仅记录目标模式参数，恢复时再启动
            if (this.#isPaused) {
                if (this.#mode === 'countdown') {
                    this.#prepareCountdown(this.interval)
                }
                return
            }

            if (this.#mode === 'video') {
                this.#startVideoWaiting(item)
            } else {
                this.#startCountdown(this.interval)
            }
        }

        #teardownAutoplay = () => {
            this.#clearCountdown()
            this.#clearVideoWaiting()
            this.#unbindPause?.()
            this.#unbindPause = null
            this.#mode = null
        }

        /* ================= 倒计时模式 ================= */

        #prepareCountdown = duration => {
            this.#mode = 'countdown'
            this.#countdownDuration = duration
            this.#countdownElapsed = 0
            this.#countdownStart = performance.now()
        }

        #startCountdown = duration => {
            this.#prepareCountdown(duration)
            this.#tick()
        }

        #clearCountdown = () => {
            if (this.#rafId) {
                cancelAnimationFrame(this.#rafId)
                this.#rafId = null
            }
        }

        #tick = () => {
            if (this.#isPaused || !this.#countdownDuration) return

            const elapsed = performance.now() - this.#countdownStart

            // 拖拽中不推进进度、不切换，继续下一帧
            if (this._isDragging) {
                this.#rafId = requestAnimationFrame(this.#tick)
                return
            }

            const pct = Math.min(1, elapsed / this.#countdownDuration)
            this.#setActiveIndicatorProgress(pct)

            if (elapsed >= this.#countdownDuration) {
                this.#advance()
                return
            }

            this.#rafId = requestAnimationFrame(this.#tick)
        }

        #advance = () => {
            this.#move(1, true)
        }

        /* ================= 视频模式 ================= */

        #startVideoWaiting = item => {
            const video = this.#getRenderedVideo(item)
            if (video) {
                this.#bindVideo(video)
                return
            }

            // ly-video 懒渲染，等待 <video> 出现
            if (this.#videoObserver) return

            this.#videoObserver = new MutationObserver(() => {
                const rendered = this.#getRenderedVideo(item)
                if (!rendered) return

                this.#videoObserver?.disconnect()
                this.#videoObserver = null
                this.#bindVideo(rendered)
            })
            this.#videoObserver.observe(item, { childList: true, subtree: true })
        }

        #getRenderedVideo = item => item.querySelector('ly-video [data-ly-video-rendered]')

        #updateVideoProgress = () => {
            const video = this.#videoEl?.video
            if (!video) return

            const { duration } = video
            const pct = Number.isFinite(duration) && duration > 0
                ? Math.min(1, video.currentTime / duration)
                : 0

            this.#setActiveIndicatorProgress(pct)
        }

        #bindVideo = video => {
            this.#videoEl = { video }

            video.addEventListener('play', this.#handleVideoPlay)
            video.addEventListener('timeupdate', this.#handleVideoTimeupdate)
            video.addEventListener('ended', this.#handleVideoEnded)
            video.addEventListener('loadedmetadata', this.#handleVideoLoadedMetadata)

            this.#updateVideoProgress()
        }

        #clearVideoWaiting = () => {
            this.#videoObserver?.disconnect()
            this.#videoObserver = null

            const video = this.#videoEl?.video
            if (video) {
                video.removeEventListener('play', this.#handleVideoPlay)
                video.removeEventListener('timeupdate', this.#handleVideoTimeupdate)
                video.removeEventListener('ended', this.#handleVideoEnded)
                video.removeEventListener('loadedmetadata', this.#handleVideoLoadedMetadata)
            }
            this.#videoEl = null
        }

        #handleVideoPlay = () => {
            if (this.#isPaused || this._isDragging) return
            this.#updateVideoProgress()
        }

        #handleVideoTimeupdate = () => {
            if (this.#isPaused || this._isDragging) return

            const video = this.#videoEl?.video
            if (!video) return

            // 直播流等无法判定结尾 → 退化为 interval 倒计时
            if (!Number.isFinite(video.duration)) {
                this.#fallbackToCountdown()
                return
            }

            this.#updateVideoProgress()

            // 即将播放到结尾（将循环回 0）→ 视为完成一次播放
            if (video.currentTime >= video.duration - 0.3) {
                this.#advance()
            }
        }

        #handleVideoEnded = () => {
            if (this.#isPaused || this._isDragging) return
            this.#advance()
        }

        #handleVideoLoadedMetadata = () => {
            if (this.#isPaused) return
            this.#updateVideoProgress()
        }

        #fallbackToCountdown = () => {
            this.#clearVideoWaiting()

            if (this.#isPaused) {
                this.#prepareCountdown(this.interval)
                return
            }

            this.#startCountdown(this.interval)
        }

        #resetVideoIfPlaying = item => {
            const video = this.#getRenderedVideo(item)
            if (!video || video.paused || video.ended) return

            // 正在播放 → 进度清零重播
            video.currentTime = 0
        }

        /* ================= 指示器进度 ================= */

        #resetIndicators = () => {
            this._indicators.forEach(el => el.style.setProperty('--progress', 0))
        }

        #setActiveIndicatorProgress = pct => {
            this._indicators.forEach((indicator, index) => {
                if (index % this._items.length === this._index) {
                    indicator.style.setProperty('--progress', pct)
                }
            })
        }

        /* ================= 暂停 / 恢复 ================= */

        #bindPauseTriggers = () => {
            const handleVisibility = () => {
                if (document.hidden) {
                    this.#freezeAutoplay()
                } else {
                    this.#resumeAutoplay()
                }
            }

            document.addEventListener('visibilitychange', handleVisibility)

            this.#unbindPause = () => {
                document.removeEventListener('visibilitychange', handleVisibility)
            }
        }

        #freezeAutoplay = () => {
            if (this.#isPaused) return
            this.#isPaused = true

            // 倒计时模式：冻结进度，记录已耗时
            if (this.#mode === 'countdown') {
                this.#countdownElapsed = Math.min(performance.now() - this.#countdownStart, this.#countdownDuration)
                this.#clearCountdown()
            }
            // 视频模式：保留监听，处理器因 #isPaused 直接返回（进度冻结、不切换）
        }

        #resumeAutoplay = () => {
            if (!this.#isPaused) return
            this.#isPaused = false

            if (!this.#autoplayEnabled || !this.#mode) return

            if (this.#mode === 'countdown') {
                // 从冻结位置继续
                this.#countdownStart = performance.now() - this.#countdownElapsed
                this.#tick()
                return
            }

            // 视频模式
            if (this.#videoEl) {
                this.#updateVideoProgress()
                return
            }

            if (!this.#videoObserver) {
                this.#startVideoWaiting(this._items[this._index])
            }
        }
    })
}

if (!customElements.get('ly-index-banner-content-activity-end-time')) {
    customElements.define('ly-index-banner-content-activity-end-time', class extends HTMLElement {

        connectedCallback() {
            this.endTime = this.getAttribute('data-end-time')
            if (!this.endTime) return

            this.dayUnit = this.getAttribute('data-day-unit') || 'd'
            this.hurUnit = this.getAttribute('data-hur-unit') || 'h'
            this.minUnit = this.getAttribute('data-min-unit') || 'm'
            this.secUnit = this.getAttribute('data-sec-unit') || 's'

            this.dayEl = this.querySelector('.t.day')
            this.hurEl = this.querySelector('.t.hur')
            this.minEl = this.querySelector('.t.min')
            this.secEl = this.querySelector('.t.sec')

            this.update()
            this.timer = setInterval(() => this.update(), 1000)
        }

        disconnectedCallback() {
            if (this.timer) {
                clearInterval(this.timer)
                this.timer = null
            }
        }

        update() {
            let target = new Date(this.endTime).getTime()
            if (!Number.isFinite(target)) return

            const now = Date.now()

            // 倒计时结束后向后推一个周，直到目标时间在未来
            while (target <= now) {
                target += 7 * 24 * 60 * 60 * 1000
            }

            const totalSec = Math.floor((target - now) / 1000)

            const days = `${Math.floor(totalSec / 86400)}`.padStart(2, '0')
            const hours = `${Math.floor((totalSec % 86400) / 3600)}`.padStart(2, '0')
            const minutes = `${Math.floor((totalSec % 3600) / 60)}`.padStart(2, '0')
            const seconds = `${totalSec % 60}`.padStart(2, '0')

            if (this.dayEl) this.dayEl.textContent = `${days}${this.dayUnit}`
            if (this.hurEl) this.hurEl.textContent = `${hours}${this.hurUnit}`
            if (this.minEl) this.minEl.textContent = `${minutes}${this.minUnit}`
            if (this.secEl) this.secEl.textContent = `${seconds}${this.secUnit}`
        }
    })
}
